import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, Fish, Trophy, Calendar, Target } from "lucide-react";
import { useEffect } from "react";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

const fetchUserStats = async (userId: string | undefined) => {
  if (!userId) return null;
  
  const { data, error } = await supabase
    .from("catches")
    .select("*")
    .eq("angler_id", userId);

  if (error) throw error;
  return data || [];
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82ca9d"];

export default function Statistics() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    } else if (user) {
      trackEvent({ eventName: AnalyticsEvents.STATISTICS_VIEW });
    }
  }, [user, authLoading, navigate]);

  const { data: catches = [], isLoading } = useQuery({
    queryKey: ["user-stats", user?.id],
    queryFn: () => fetchUserStats(user?.id),
    enabled: !!user,
    staleTime: 2 * 60 * 1000,
  });

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-8">
          <div className="flex items-center gap-3 mb-8">
            <Skeleton className="h-10 w-10 rounded" />
            <Skeleton className="h-10 w-64" />
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4" />
                </CardHeader>
                <CardContent className="space-y-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-2 mb-8">
            {[1, 2].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-[300px] w-full" />
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mb-8">
            <CardHeader>
              <Skeleton className="h-6 w-64" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[300px] w-full" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-56" />
            </CardHeader>
            <CardContent className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="flex justify-between">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-2 w-full rounded-full" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Calculate statistics
  const totalCatches = catches.length;
  const verifiedCatches = catches.filter((c) => c.status === "verified");
  const totalWeight = catches.reduce((sum, c) => sum + (Number(c.weight_lbs) || 0), 0);
  const avgWeight = totalCatches > 0 ? totalWeight / totalCatches : 0;
  const heaviestCatch = catches.length > 0 
    ? catches.reduce((max, c) => 
        (Number(c.weight_lbs) || 0) > (Number(max.weight_lbs) || 0) ? c : max
      , catches[0])
    : null;

  // Species distribution
  const speciesCount: { [key: string]: number } = {};
  catches.forEach((c) => {
    speciesCount[c.fish_species] = (speciesCount[c.fish_species] || 0) + 1;
  });
  const speciesData = Object.entries(speciesCount).map(([name, value]) => ({
    name,
    value,
  }));

  // Weight over time
  const catchesByDate = catches
    .map((c) => ({
      date: new Date(c.date_caught).toLocaleDateString(),
      weight: Number(c.weight_lbs) || 0,
      species: c.fish_species,
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Monthly catches
  const monthlyData: { [key: string]: number } = {};
  catches.forEach((c) => {
    const month = new Date(c.date_caught).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    monthlyData[month] = (monthlyData[month] || 0) + 1;
  });
  const monthlyChartData = Object.entries(monthlyData).map(([month, count]) => ({
    month,
    catches: count,
  }));

  // Top species by weight
  const speciesWeight: { [key: string]: number } = {};
  catches.forEach((c) => {
    const weight = Number(c.weight_lbs) || 0;
    speciesWeight[c.fish_species] = (speciesWeight[c.fish_species] || 0) + weight;
  });
  const topSpecies = Object.entries(speciesWeight)
    .map(([species, weight]) => ({ species, weight }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-8">
        <div className="flex items-center gap-3 mb-8">
          <TrendingUp className="h-10 w-10 text-primary" />
          <h1 className="text-4xl font-bold text-gradient">My Statistics</h1>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Catches
              </CardTitle>
              <Fish className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalCatches}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {verifiedCatches.length} verified
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Weight
              </CardTitle>
              <Target className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{totalWeight.toFixed(1)} lbs</div>
              <p className="text-xs text-muted-foreground mt-1">
                Avg: {avgWeight.toFixed(1)} lbs
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Heaviest Catch
              </CardTitle>
              <Trophy className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {heaviestCatch?.weight_lbs?.toFixed(1) || 0} lbs
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {heaviestCatch?.fish_species || "N/A"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Species Caught
              </CardTitle>
              <Calendar className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{Object.keys(speciesCount).length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Different species
              </p>
            </CardContent>
          </Card>
        </div>

        {catches.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Fish className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No catches yet</h3>
              <p className="text-muted-foreground">
                Start recording catches to see your statistics!
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Charts Grid */}
            <div className="grid gap-6 md:grid-cols-2 mb-8">
              {/* Monthly Catches */}
              <Card>
                <CardHeader>
                  <CardTitle>Catches Per Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="catches" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Species Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Species Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={speciesData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => 
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {speciesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Weight Progression */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Weight Progression Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={catchesByDate}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'Weight (lbs)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="weight" 
                      stroke="#0088FE" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Species by Total Weight */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Species by Total Weight</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topSpecies.map((item, index) => (
                    <div key={item.species} className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-semibold">{item.species}</span>
                          <span className="text-sm text-muted-foreground">
                            {item.weight.toFixed(1)} lbs
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${(item.weight / topSpecies[0].weight) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
